import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Layout from '@/components/layout/Layout';
import { AuthProvider } from '@/context/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import Contacts from '@/pages/Contacts';
import ContactDetail from '@/pages/ContactDetail';
import Devis from '@/pages/Devis';
import DevisDetail from '@/pages/DevisDetail';
import Factures from '@/pages/Factures';
import FactureDetail from '@/pages/FactureDetail';
import Chantiers from '@/pages/Chantiers';
import ChantierDetail from '@/pages/ChantierDetail';
import SousTraitants from '@/pages/SousTraitants';
import SousTraitantDetail from '@/pages/SousTraitantDetail';
import TarifsComparateur from '@/pages/TarifsComparateur';
import Administratif from '@/pages/Administratif';
import Parametres from '@/pages/Parametres';
import WhatsAppAdmin from '@/pages/WhatsAppAdmin';
import FacturesAchat from '@/pages/FacturesAchat';
import FactureAchatDetail from '@/pages/FactureAchatDetail';
import AIModule from '@/pages/AIModule';
import AgentsHub from '@/pages/AgentsHub';
import GestionClients from '@/pages/GestionClients';
import PortailClient from '@/pages/PortailClient';
import FichesVisite from '@/pages/FichesVisite';
import AdminUsers from '@/pages/AdminUsers';
import ActivityLog from '@/pages/ActivityLog';
import SignContract from '@/pages/SignContract';
import SignMission from '@/pages/SignMission';
import Tasks from '@/pages/Tasks';
import TaskDetail from '@/pages/TaskDetail';
import Chat from '@/pages/Chat';
import Prospections from '@/pages/Prospections';
import MarketingStudio from '@/pages/MarketingStudio';
import MarketingStudioAdvanced from '@/pages/MarketingStudioAdvanced';
import Fournisseurs from '@/pages/Fournisseurs';
import SignConvention from '@/pages/SignConvention';
import SignAoh from '@/pages/SignAoh';
import SignCadreContribution from '@/pages/SignCadreContribution';
import BookCall from '@/pages/BookCall';
import PublicBooking from '@/pages/PublicBooking';
import DepotDocuments from '@/pages/DepotDocuments';
import StRenewalPage from '@/pages/StRenewalPage';
import ProspectOnboardingPage from '@/pages/ProspectOnboardingPage';
import PreqQuizPage from '@/pages/PreqQuizPage';
import Agenda from '@/pages/Agenda';
import Comptabilite from '@/pages/Comptabilite';
import CeeSimulations from '@/pages/CeeSimulations';
import CeeSimulationWizard from '@/pages/CeeSimulationWizard';
import CeeDossiers from '@/pages/CeeDossiers';
import CeeDossierDetail from '@/pages/CeeDossierDetail';
import MetaSyncSettings from '@/pages/MetaSyncSettings';
import AdminAmos from '@/pages/AdminAmos';
import CeeZonesView from '@/pages/CeeZonesView';

function App() {
  return (
    <div className="App notranslate" translate="no">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
            {/* Public — client portal */}
            <Route path="/portail" element={<PortailClient />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signature/:token" element={<SignContract />} />
            <Route path="/mission/sign/:token" element={<SignMission />} />
            <Route path="/prospections/convention/sign/:token" element={<SignConvention />} />
            <Route path="/sign/aoh/:token" element={<SignAoh />} />
            <Route path="/sign/cadre/:token" element={<SignCadreContribution />} />
            <Route path="/book/call/:token" element={<BookCall />} />
            <Route path="/rdv/:slug" element={<PublicBooking />} />
            <Route path="/depot/:token" element={<DepotDocuments />} />
            <Route path="/st-renewal/:token" element={<StRenewalPage />} />
            <Route path="/prospect/onboarding/:token" element={<ProspectOnboardingPage />} />
            <Route path="/preq/:token" element={<PreqQuizPage />} />

            {/* CRM — gated by ProtectedRoute */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="comptabilite" element={<Comptabilite />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              <Route path="devis" element={<Devis />} />
              <Route path="devis/:id" element={<DevisDetail />} />
              <Route path="factures" element={<Factures />} />
              <Route path="factures/:id" element={<FactureDetail />} />
              <Route path="factures-achat" element={<FacturesAchat />} />
              <Route path="factures-achat/:id" element={<FactureAchatDetail />} />
              <Route path="chantiers" element={<Chantiers />} />
              <Route path="chantiers/:id" element={<ChantierDetail />} />
              <Route path="sous-traitants" element={<SousTraitants />} />
              <Route path="sous-traitants/comparateur" element={<TarifsComparateur />} />
              <Route path="sous-traitants/:id" element={<SousTraitantDetail />} />
              <Route path="gestion-clients" element={<GestionClients />} />
              <Route path="fiches-visite" element={<FichesVisite />} />
              <Route path="ia" element={<AIModule />} />
              <Route path="agents" element={<AgentsHub />} />
              <Route path="administratif" element={<Administratif />} />
              <Route path="taches" element={<Tasks />} />
              <Route path="taches/:id" element={<TaskDetail />} />
              <Route path="discussions" element={<Chat />} />
              <Route path="prospections" element={<Prospections />} />
              <Route path="marketing" element={<MarketingStudio />} />
              <Route path="marketing/advanced" element={<MarketingStudioAdvanced />} />
              <Route path="fournisseurs" element={<Fournisseurs />} />
              {/* CEE V1 */}
              <Route path="cee/simulations" element={<CeeSimulations />} />
              <Route path="cee/simulations/new" element={<CeeSimulationWizard />} />
              <Route path="cee/simulations/:id" element={<CeeSimulationWizard />} />
              <Route path="cee/dossiers" element={<CeeDossiers />} />
              <Route path="cee/zones" element={<CeeZonesView />} />
              <Route path="cee/dossiers/:id" element={<CeeDossierDetail />} />
              <Route path="settings/meta-sync" element={<MetaSyncSettings />} />
              {/* Admin-only sections */}
              <Route path="admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
              <Route path="admin/amos" element={<ProtectedRoute role="admin"><AdminAmos /></ProtectedRoute>} />
              <Route path="admin/activity-log" element={<ProtectedRoute role="admin"><ActivityLog /></ProtectedRoute>} />
              {/* Legacy redirects */}
              <Route path="marketing" element={<Navigate to="/" replace />} />
              <Route path="integrations" element={<Navigate to="/" replace />} />
              <Route path="parametres" element={<ProtectedRoute role="admin"><Parametres /></ProtectedRoute>} />
              <Route path="whatsapp/admin" element={<ProtectedRoute role="admin"><WhatsAppAdmin /></ProtectedRoute>} />
            </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
